<template>
  <div v-if="isOpen" class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h2 class="modal-title">Gestor de Imágenes</h2>
        <button @click="closeModal" class="close-button">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <!-- Subir nueva imagen -->
        <div class="upload-section mb-4">
          <div class="upload-controls">
            <button type="button" @click="openFileExplorer" class="upload-button">
              <i class="fas fa-cloud-upload-alt"></i>
              Subir nueva imagen
            </button>
            <button type="button" @click="loadImages" class="reload-button" :disabled="loading">
              <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
              Recargar
            </button>
          </div>
          <input 
            type="file" 
            ref="fileInput" 
            @change="uploadImage" 
            accept="image/*" 
            class="file-input" 
          />
        </div>

        <!-- Grid de imágenes -->
        <div v-if="loading" class="loading-container">
          <div class="loading-spinner">
            <i class="fas fa-spinner fa-spin"></i>
            <p>Cargando imágenes...</p>
          </div>
        </div>

        <div v-else-if="images.length === 0" class="no-images-container">
          <div class="no-images-message">
            <i class="fas fa-images"></i>
            <p>No hay imágenes disponibles</p>
            <small>Sube una imagen para comenzar</small>
          </div>
          <div class="debug-info">
            <details>
              <summary>Información de Debug</summary>
              <div class="debug-content">
                <p><strong>Endpoint probado:</strong> /laravel-filemanager/jsonitems?type=image</p>
                <p><strong>Estado de carga:</strong> {{ loading ? 'Cargando...' : 'Completado' }}</p>
                <p><strong>Número de imágenes:</strong> {{ images.length }}</p>
                <p><strong>Revisa la consola del navegador</strong> para más detalles</p>
              </div>
            </details>
          </div>
        </div>

        <div v-else class="images-grid">
          <!-- Debug: mostrar información de las imágenes -->

          <div v-for="(img, index) in images" :key="img.name" class="image-item"
            :class="{ 'selected': selected === img.url }">
            
            <div class="image-container" @click="selectImage(img.url)">
              <img :src="img.thumb_url || img.url" class="image-thumbnail" :alt="img.name"
                @error="handleImageError(index)" @load="handleImageLoad(index)" />
            </div>
          </div>
        </div>

        <div v-if="images.length > 0" class="pagination-controls">
          <button @click="prevPage" :disabled="currentPage === 1 || loading" class="pagination-button">Anterior</button>
          <span class="pagination-info">Página {{ currentPage }} de {{ totalPages }}</span>
          <button @click="nextPage" :disabled="loading || currentPage === totalPages" class="pagination-button">Siguiente</button>
        </div>

        <!-- Imagen seleccionada -->
        <div v-if="selected" class="selected-image-section">
          <h3 class="selected-title">Imagen seleccionada:</h3>
          <div class="selected-image-container">
            <img :src="selected" class="selected-image" />
            <button @click="confirmSelection" class="confirm-button">
              <i class="fas fa-check"></i> Confirmar selección
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch } from 'vue';
import axios from '@/plugins/axios';
import Swal from 'sweetalert2';

export default {
  name: "ImageManagerModal",
  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'image-selected'],
  setup(props, { emit }) {
    const images = ref([]);
    const selected = ref(null);
    const loading = ref(false);
    const currentPage = ref(1);
    const totalPages = ref(1);
    const fileInput = ref(null);

    // Watcher para debuggear cambios en el array de imágenes
    watch(images, (newImages, oldImages) => {
      console.log('🔄 Array de imágenes cambió:');
      console.log('Antes:', oldImages);
      console.log('Después:', newImages);
      console.log('Longitud nueva:', newImages.length);
    }, { deep: true });

    // Watcher para cuando se abre el modal
    watch(() => props.isOpen, (isOpen) => {
      if (isOpen) {
        console.log('🚪 Modal abierto, cargando imágenes...');
        loadImages();
      } else {
        console.log('🚪 Modal cerrado, limpiando estado...');
        images.value = [];
        selected.value = null;
      }
    });

    const backendBaseUrl = (
      process.env.VUE_APP_ACARA_WEB_BACKEND_URL ||
      process.env.VUE_APP_KEYCLOAK_API_BASE_URL ||
      'http://127.0.0.1:8001/api/v1'
    ).replace('/api/v1', '');

    const fixUrl = (url) => {
      if (!url) return url;
      try {
        const parsed = new URL(url);
        const backend = new URL(backendBaseUrl);
        parsed.host = backend.host;
        parsed.protocol = backend.protocol;
        return parsed.toString();
      } catch {
        return url;
      }
    };

    const loadImages = async (page = 1) => {
      try {
        currentPage.value = page;
        loading.value = true;

        const res = await axios.get(`/laravel-filemanager/jsonitems?type=image&page=${page}`);

        // Filtrar solo las imágenes (excluir carpetas)
        let allItems = [];
        if (res.data && Array.isArray(res.data)) {
          allItems = res.data;
        } else if (res.data && res.data.items && Array.isArray(res.data.items)) {
          allItems = res.data.items;
        } else {
          console.log('Estructura de respuesta no reconocida:', res.data);
          allItems = [];
        }

        // Filtrar solo elementos que son imágenes (tienen extensión de imagen)
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
        images.value = allItems
          .filter(item => {
            if (item.type === 'folder' || item.type === 'directory') return false;
            const fileName = item.name || item.basename || '';
            const hasImageExtension = imageExtensions.some(ext =>
              fileName.toLowerCase().endsWith(ext)
            );
            const hasImageUrl = item.url && imageExtensions.some(ext =>
              item.url.toLowerCase().includes(ext)
            );
            return hasImageExtension || hasImageUrl;
          })
          .map(item => ({
            ...item,
            url: fixUrl(item.url),
            thumb_url: fixUrl(item.thumb_url),
          }));

        console.log('Elementos totales:', allItems.length);
        console.log('Imágenes filtradas:', images.value.length);

        // Calcular totalPages desde paginator
        if (res.data.paginator) {
          currentPage.value = res.data.paginator.current_page;
          totalPages.value = Math.ceil(res.data.paginator.total / res.data.paginator.per_page);
        } else {
          currentPage.value = 1;
          totalPages.value = 1;
        }

        console.log('Estado final de imágenes:', images.value);
        console.log('Número de imágenes:', images.value.length);

        // Debug: mostrar la primera imagen
        if (images.value.length > 0) {
          console.log('Primera imagen:', images.value[0]);
          console.log('URL de la primera imagen:', images.value[0].url);
          console.log('Thumbnail de la primera imagen:', images.value[0].thumb_url);
        }

      } catch (err) {
        console.error('Error cargando imágenes:', err);
        console.error('Detalles del error:', err.response?.data);
        images.value = [];
      } finally {
        loading.value = false;
      }
    };

    const openFileExplorer = () => {
      fileInput.value.click();
    };

    const uploadImage = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('upload', file);

      try {
        loading.value = true;
        const response = await axios.post('/laravel-filemanager/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        console.log('Respuesta del servidor:', response.data);
        
        // Recargar la lista para obtener la imagen recién subida
        await loadImages();
        
        // Usar directamente la URL que devuelve el servidor
        if (response.data && response.data.url) {
          emit('image-selected', fixUrl(response.data.url));
          closeModal();
        } else {
          console.log('No se recibió URL del servidor');
        }
        
      } catch (err) {
        console.error('Error subiendo imagen:', err);
      } finally {
        loading.value = false;
        // Limpiar el input para permitir seleccionar el mismo archivo nuevamente
        event.target.value = '';
      }
    };

    const selectImage = (url) => {
      selected.value = url;
      // Solo cerrar el modal si se selecciona una imagen existente
      // No cerrar si se acaba de subir una nueva imagen
      emit('image-selected', url);
      closeModal();
    };

    const testImages = () => {
      console.log('🧪 Test del estado de imágenes:');
      console.log('Estado actual de images:', images.value);
      console.log('Tipo de images:', typeof images.value);
      console.log('¿Es array?', Array.isArray(images.value));
      console.log('Longitud:', images.value.length);

      if (images.value.length > 0) {
        console.log('Primera imagen:', images.value[0]);
        console.log('Propiedades de la primera imagen:', Object.keys(images.value[0]));
      }

      // Forzar una actualización del estado
      images.value = [...images.value];
    };

    const handleImageLoad = (index) => {
      console.log(`✅ Imagen ${index} cargada exitosamente:`, images.value[index]);
    };

    const handleImageError = (index) => {
      console.error(`Error cargando imagen ${index}:`, images.value[index]);
    };

    const deleteImage = async (img, index) => {
      // Confirmar antes de eliminar
      const result = await Swal.fire({
        title: '¿Eliminar imagen?',
        text: `¿Estás seguro de que quieres eliminar "${img.name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      });

      if (result.isConfirmed) {
        try {
          loading.value = true;
          const backendBaseUrl = process.env.VUE_APP_ACARA_WEB_BACKEND_URL ?? 'http://127.0.0.1:8000/api/v1';
        
           const backendUrl = backendBaseUrl.replace('/api/v1', '');
          const deleteUrl = `${backendUrl}/laravel-filemanager/delete`;
                // Llamar a la API para eliminar la imagen
          const formData = new FormData();
          formData.append('items[]', img.name); // img.name = ruta relativa completa

          await axios.post(deleteUrl, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          
          // Eliminar de la lista local
          images.value.splice(index, 1);
          
          // Si la imagen eliminada estaba seleccionada, limpiar la selección
          if (selected.value === img.url) {
            selected.value = null;
          }
          
          await Swal.fire({
            icon: 'success',
            title: 'Imagen eliminada',
            text: 'La imagen se eliminó correctamente',
            timer: 1500,
            showConfirmButton: false
          });
          
        } catch (err) {
          console.error('Error eliminando imagen:', err);
          await Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudo eliminar la imagen',
            confirmButtonColor: '#d33'
          });
        } finally {
          loading.value = false;
        }
      }
    };

    const confirmSelection = () => {
      if (selected.value) {
        emit('image-selected', selected.value);
        closeModal();
      }
    };

    const closeModal = () => {
      selected.value = null;
      emit('close');
    };

    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        loadImages(currentPage.value + 1);
      }
    };

    const prevPage = () => {
      if (currentPage.value > 1) {
        loadImages(currentPage.value - 1);
      }
    };

    return {
      images,
      selected,
      loading,
      currentPage,
      totalPages,
      fileInput,
      loadImages,
      uploadImage,
      openFileExplorer,
      selectImage,
      deleteImage,
      testImages,
      handleImageLoad,
      handleImageError,
      confirmSelection,
      closeModal,
      nextPage,
      prevPage
    };
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.25rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s;
}

.close-button:hover {
  background-color: #f3f4f6;
  color: #374151;
}

.modal-body {
  padding: 1.5rem;
}

.upload-section {
  text-align: center;
}

.upload-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  gap: 1rem;
}

.upload-button {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background-color: #3b82f6;
  border: 2px solid #3b82f6;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  color: white;
  flex-grow: 1;
  font-weight: 500;
  text-decoration: none;
}

.upload-button:hover {
  background-color: #2563eb;
  border-color: #2563eb;
  color: white;
  transform: translateY(-1px);
}

.upload-button i {
  margin-right: 0.5rem;
}

.reload-button {
  background-color: #f3f4f6;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  transition: all 0.2s;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
}

.reload-button:hover {
  background-color: #e5e7eb;
  border-color: #c4c4c4;
  color: #374151;
}

.reload-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #f3f4f6;
  border-color: #d1d5db;
  color: #9ca3af;
}

.test-button {
  background-color: #fef3c7;
  border: 2px solid #f59e0b;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  transition: all 0.2s;
  color: #92400e;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
}

.test-button:hover {
  background-color: #fde68a;
  border-color: #d97706;
  color: #78350f;
}

.file-input {
  display: none;
}

.images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.image-item {
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}

.image-item:hover {
  border-color: #3b82f6;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.image-item.selected {
  border-color: #10b981;
  background-color: #f0fdf4;
}

.image-container {
  cursor: pointer;
  position: relative;
}

.delete-image-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #dc3545;
  border: 2px solid white;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
  opacity: 0;
}

.image-item:hover .delete-image-btn {
  opacity: 1;
}

.delete-image-btn:hover {
  background-color: #c82333;
  transform: scale(1.1);
}

.debug-images-info {
  grid-column: 1 / -1;
  background-color: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 1rem;
  text-align: center;
}

.debug-images-info p {
  margin: 0.25rem 0;
  font-size: 0.875rem;
  color: #0369a1;
}

.image-debug {
  display: block;
  font-size: 0.75rem;
  color: #6b7280;
  word-break: break-all;
  margin-top: 0.25rem;
  line-height: 1.2;
}

.image-thumbnail {
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 6px;
  margin-bottom: 0.5rem;
  border: 1px solid #e5e7eb;
  transition: all 0.2s;
}

.image-thumbnail:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.image-name {
  font-size: 0.875rem;
  color: #374151;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selected-image-section {
  border-top: 1px solid #e5e7eb;
  padding-top: 1.5rem;
}

.selected-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
}

.selected-image-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.selected-image {
  width: 200px;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #10b981;
}

.confirm-button {
  background-color: #10b981;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.confirm-button:hover {
  background-color: #059669;
  transform: translateY(-1px);
}

.confirm-button i {
  font-size: 0.875rem;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem 0;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.loading-spinner i {
  font-size: 2rem;
  color: #3b82f6;
}

.loading-spinner p {
  font-size: 1rem;
  color: #6b7280;
}

.no-images-container {
  text-align: center;
  padding: 2rem 0;
}

.no-images-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.no-images-message i {
  font-size: 3rem;
  color: #e5e7eb;
}

.no-images-message p {
  font-size: 1.125rem;
  color: #4b5563;
  margin: 0;
}

.no-images-message small {
  font-size: 0.875rem;
  color: #9ca3af;
}

.debug-info {
  margin-top: 1rem;
  padding: 1rem;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #4b5563;
}

.debug-info details {
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 0.75rem;
  margin-top: 0.5rem;
}

.debug-info summary {
  font-weight: 600;
  cursor: pointer;
  color: #3b82f6;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.debug-content p {
  margin: 0.25rem 0;
}

.debug-content strong {
  color: #1f2937;
}

.image-debug-item {
  background-color: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 4px;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.75rem;
  color: #92400e;
}

.image-debug-item p {
  margin: 0.125rem 0;
  line-height: 1.2;
}

.image-debug-item strong {
  color: #78350f;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.pagination-button {
  background-color: #f3f4f6;
  border: 2px solid #d1d5db;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
  color: #374151;
}

.pagination-button:hover:not(:disabled) {
  background-color: #e5e7eb;
}

.pagination-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  font-weight: 600;
  color: #1f2937;
}

@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    margin: 1rem;
  }

  .images-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 0.75rem;
  }

  .image-thumbnail {
    height: 100px;
  }
}
</style>
